import { isString } from 'lodash-es';
import styled, { keyframes } from 'styled-components';

import colors from 'utils/colors';
import device from 'utils/device';
import * as Styling from 'utils/styled';

import { ItemComponentProps } from './interfaces';

export const GroupLabel = styled.legend`
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.5;
  margin-bottom: 16px;
  color: ${colors.blueGrey};
`;

const easeOut = keyframes`
  from {
    transform: scale(0.5);
  }
  to {
    transform: scale(1,1);
  }
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const FieldsetWrapper = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
`;

export const ItemWrapper = styled.div<{ hasSubtitle: boolean }>`
  width: 100%;
  padding-bottom: ${props => (props.hasSubtitle ? '3rem' : '1.5rem')};

  @media ${device.isDesktop}, ${device.isTablet} {
    padding-bottom: ${props => (props.hasSubtitle ? '2.5rem' : '1rem')};
  }
`;

// Styling using the solution from https://codepen.io/artilishes/pen/QEjzwm
export const Input = styled.input`
  /* Hide the default radio button icon */
  opacity: 0;
  position: absolute;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

export const Label = styled.label<ItemComponentProps>`
  display: inline-flex;
  align-items: center;
  ${props => isString(props.label) && 'height: 1.5rem'};
  font-size: 1rem;
  font-weight: ${props => (props.subtitle ? '600' : 'normal')};
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: -0.32px;
  cursor: pointer;

  @media ${device.isMobile} {
    max-width: 300px;
  }

  ${Input}:focus + & {
    outline: 2px solid ${colors.blueGrey25};
    outline-offset: 4px;
  }

  ${Input}:disabled + & {
    cursor: not-allowed;
  }
`;

export const InnerCircle = styled.span`
  border-radius: inherit;
  width: 14px;
  height: 14px;
  background-color: ${colors.white};

  ${Input}:checked + ${Label} & {
    animation: ${easeOut} 0.15s ease-out;
  }

  // Checked, not disabled - orange circle
  ${Input}:checked:not(:disabled) + ${Label} & {
    background-color: ${colors.orange};
  }

  // Checked & disabled - dark grey circle
  ${Input}:checked:disabled + ${Label} & {
    background-color: ${colors.darkGrey75};
  }

  // Disabled & not checked -> No background
  ${Input}:disabled + ${Label} & {
    background-color: transparent;
  }
`;

export const Circle = styled.span`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 24px;
  transform: scale(0.85);
  flex: none;
  border: 1px solid ${colors.blueGrey};
  margin: 1px 12px 1px 1px;

  // If input checked, always has a thick border
  ${Input}:checked + ${Label} & {
    border-width: 2px;
    margin: 0 11px 0 0;
  }

  // When not disabled and checked, orange border
  ${Input}:checked:not(:disabled) + ${Label} & {
    border-color: ${colors.orange};
  }

  // When hovered & not disabled have orange border, no explicit thickness (as could be checked/unchecked)
  ${Input}:not(:disabled) + ${Label}:hover & {
    border-color: ${colors.orange};
    cursor: pointer;
  }

  // When disabled, tint background and prevent clicks
  ${Input}:disabled + ${Label} & {
    background-color: ${colors.darkGrey10};
    pointer-events: none;
  }
`;

export const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: start;
`;

export const Text = styled.span`
  color: ${colors.blueGrey};

  ${Input}:not(:disabled) + ${Label}:hover & {
    ${Styling.textFocus}
  }
`;

export const Subtitle = styled.span<ItemComponentProps>`
  color: ${colors.blueGrey};
  font-weight: normal;
  opacity: 0.9;

  ${Input}:not(:disabled) + ${Label}:hover & {
    ${Styling.textFocus}
  }
`;
